// Ports ResidentProfile/selectors.js's getHouseholdPendingMembers to a pure,
// Redux-free function. Keep this in lockstep with that selector until it's
// retired.
//
// Reads from `residentQuery.data.applicants.{pendingApplicants,canceledApplicants}`
// — the raw `applicants` field on ResidentService.getResident's response,
// which is NOT the same as `applicantInfo.applicants` used by
// useHouseholdMembersQuery (the Redux reducer stores both, unrenamed, as two
// separate top-level state keys).
import { parseResidentInfo } from './useHouseholdMembersQuery.helpers';

export const buildHouseholdPendingMembers = ({
  pendingApplicants = [],
  canceledApplicants = [],
}) => {
  const pendingAdultMembers = pendingApplicants
    .filter((a) => a?.applicantCustomer != null)
    .map((adult) => {
      // The raw applicant record already has enough data to populate the
      // Mini-Profile's Personal/Affordable sections — reuse the same parser
      // Current/Prior members use. isPrimary is false by construction: these rows are
      // pending lease addendums, so the household already has a Current Head of Household.
      const residentInfo = parseResidentInfo(
        adult,
        ['applicantCustomer', 'customer'],
        'adult',
        false,
        adult?.relationshipId ?? 'default',
      );
      return {
        id: adult.id,
        applicantId: adult.id,
        customerId: adult.applicantCustomer.customer.id,
        name: [
          adult.applicantCustomer.customer.firstName,
          adult.applicantCustomer.customer.lastName,
          adult.applicantCustomer.customer.suffix
            ? adult.applicantCustomer.customer.suffix.name
            : '',
        ].join(' '),
        status: 'Pending',
        wasMinor: adult?.wasMinor ?? false,
        // Normalized to 'adult' (not the raw applicantType.name, e.g. "Head")
        // to match useMiniProfileSections/buildSavePayload's isAdult check —
        // ResidentProfile#editOccupant's eligibility check
        // (['adult','minor','child'].includes(type)) also depends on this.
        type: 'adult',
        relationship: adult?.relationship?.name ?? '---',
        residentInfo,
        // ENG-30290 -- Residents.js#getChecklistedApplicants (backend)
        // already attaches `{ emails: [...], phones: [...] }` (ContactEmail/
        // ContactPhone rows, including the primary) onto every pending
        // applicant record here, same shape/field name as the Current/Prior
        // mapper (useHouseholdMembersQuery.helpers.js). Surfacing it here is
        // what lets useMiniProfileSections.js's getDefaultValues pre-populate
        // Secondary/Tertiary Phone/Email for a Pending adult, and what lets
        // buildSavePayload.js's buildPendingApplicantSavePayload correctly
        // detect "this member already had additional entries" so an
        // unrelated Save re-sends them instead of silently deleting them via
        // the full-array-replace contract.
        contactInformation: adult.contactInformation,
      };
    });

  const pendingPetMembers = pendingApplicants
    .filter((a) => a?.applicantPet != null)
    .map((pet) => ({
      id: pet.id,
      name: `${pet.applicantPet.name}`,
      relationship: pet.applicantPet.petType
        ? pet.applicantPet.petType.name
        : '---',
      type: 'pet',
      status: 'Pending',
    }));

  const canceledAdultMembers = canceledApplicants
    .filter((a) => a?.applicantCustomer != null)
    .map((adult) => ({
      id: adult.id,
      name: [
        adult.applicantCustomer.customer.firstName,
        adult.applicantCustomer.customer.lastName,
        adult.applicantCustomer.customer.suffix
          ? adult.applicantCustomer.customer.suffix.name
          : '',
      ].join(' '),
      status: 'Denied',
      type: adult.applicantType.name,
      relationship: adult?.relationship?.name ?? '---',
    }));

  const canceledPetMembers = canceledApplicants
    .filter((a) => a?.applicantPet != null)
    .map((pet) => ({
      id: pet.id,
      name: `${pet.applicantPet.name}`,
      relationship: pet.applicantPet.petType
        ? pet.applicantPet.petType.name
        : '---',
      type: 'pet',
      status: 'Denied',
    }));

  // Order preserved exactly as ResidentProfile/selectors.js's
  // getHouseholdPendingMembers: pending pets sort before canceled pets,
  // but canceled pets sort before canceled adults.
  return [
    ...pendingAdultMembers,
    ...pendingPetMembers,
    ...canceledPetMembers,
    ...canceledAdultMembers,
  ];
};
